import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  APIProvider,
  AdvancedMarker,
  Map as GoogleMap,
  useMap,
  type MapCameraChangedEvent,
} from '@vis.gl/react-google-maps'
import { cn } from '@asad/crane-ui'

import { GOOGLE_MAPS_KEY, MAP_ID } from '../config/googleMaps'
import { alertStatusOf } from '../config/statusConfig'
import { type MapCameraRef, useMapPosition } from '../hooks/useMapPosition'
import { DeviceStatusType, MapDevice } from '../types'
import { DeviceMarker } from './DeviceMarker'

type PositionedDevice = MapDevice & { latitude: number; longitude: number }

const SELECT_ZOOM = 11

const hasCoords = (device: MapDevice): device is PositionedDevice =>
  typeof device.latitude === 'number' &&
  Number.isFinite(device.latitude) &&
  typeof device.longitude === 'number' &&
  Number.isFinite(device.longitude)

interface PanToSelectedProps {
  devices: PositionedDevice[]
  clickedChip?: string
  cameraRef: MapCameraRef
}

const PanToSelected = ({ devices, clickedChip, cameraRef }: PanToSelectedProps) => {
  const map = useMap()

  useEffect(() => {
    if (!map || !clickedChip) return
    const device = devices.find((item) => item.id === clickedChip)
    if (!device) return

    const center = { lat: device.latitude, lng: device.longitude }
    map.panTo(center)
    if ((map.getZoom() ?? 0) < SELECT_ZOOM) map.setZoom(SELECT_ZOOM)
    cameraRef.current.center = center
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, clickedChip])

  return null
}

interface MapContentProps {
  visibleDevices: MapDevice[]
  devicesStatuses: DeviceStatusType[]
  clickedChip?: string
  onChipClick?: (deviceId: string) => void
  onPinClick?: (deviceId: string) => void
  cameraRef: MapCameraRef
  onCameraChanged: (event: MapCameraChangedEvent) => void
}

const MapContent = ({
  visibleDevices,
  devicesStatuses,
  clickedChip,
  onChipClick,
  onPinClick,
  cameraRef,
  onCameraChanged,
}: MapContentProps) => {
  const [closedFor, setClosedFor] = useState<string | undefined>(undefined)
  useEffect(() => setClosedFor(undefined), [clickedChip])

  const handlePinClick = useCallback(
    (deviceId: string) => {
      // Reset przed `onPinClick`: przy tym samym `clickedChip` efekt wyżej się nie uruchomi.
      setClosedFor(undefined)
      onPinClick?.(deviceId)
    },
    [onPinClick],
  )

  const handleMapClick = useCallback(() => {
    if (clickedChip) setClosedFor(clickedChip)
  }, [clickedChip])

  const handleCollapse = useCallback(() => {
    if (clickedChip) setClosedFor(clickedChip)
  }, [clickedChip])

  const positioned = useMemo(() => visibleDevices.filter(hasCoords), [visibleDevices])

  const statusById = useMemo(() => new Map(devicesStatuses.map((status) => [status.id, status])), [devicesStatuses])

  const expandedId = clickedChip && closedFor !== clickedChip ? clickedChip : undefined

  return (
    <GoogleMap
      mapId={MAP_ID}
      className='size-full'
      gestureHandling='greedy'
      disableDefaultUI
      reuseMaps
      defaultZoom={cameraRef.current.zoom}
      defaultCenter={cameraRef.current.center}
      onCameraChanged={onCameraChanged}
      onClick={handleMapClick}
    >
      <PanToSelected devices={positioned} clickedChip={clickedChip} cameraRef={cameraRef} />

      {positioned.map((device) => {
        const status = statusById.get(device.id)
        const alertStatus = alertStatusOf(status)
        const attention = alertStatus === 'alarm' || alertStatus === 'error'
        const active = device.id === clickedChip

        // `clickable`: bez niego @vis.gl daje treści markera `pointer-events: none`
        // i klik / tap / hover nie docierają do `DeviceMarker`.
        return (
          <AdvancedMarker
            key={device.id}
            position={{ lat: device.latitude, lng: device.longitude }}
            zIndex={active ? 9999 : attention ? 5 : 1}
            clickable
          >
            <DeviceMarker
              device={device}
              deviceStatus={status}
              active={active}
              expanded={device.id === expandedId}
              onClick={handlePinClick}
              onClose={handleCollapse}
              onDetails={onChipClick}
            />
          </AdvancedMarker>
        )
      })}
    </GoogleMap>
  )
}

export interface CraneMapProps {
  visibleDevices: MapDevice[]
  devicesStatuses: DeviceStatusType[]
  clickedChip?: string
  onChipClick?: (deviceId: string) => void
  onPinClick?: (deviceId: string) => void
  cameraRef?: MapCameraRef
  onCameraChanged?: (event: MapCameraChangedEvent) => void
  className?: string
}

export const CraneMap = ({
  visibleDevices,
  devicesStatuses,
  clickedChip,
  onChipClick,
  onPinClick,
  cameraRef,
  onCameraChanged,
  className,
}: CraneMapProps) => {
  const ownCamera = useMapPosition()

  return (
    <div
      className={cn(
        'relative z-0 min-h-crane-map-full w-full overflow-hidden rounded-crane-sm bg-crane-card-inset',
        className,
      )}
    >
      <APIProvider apiKey={GOOGLE_MAPS_KEY}>
        <MapContent
          visibleDevices={visibleDevices}
          devicesStatuses={devicesStatuses}
          clickedChip={clickedChip}
          onChipClick={onChipClick}
          onPinClick={onPinClick}
          cameraRef={cameraRef ?? ownCamera.cameraRef}
          onCameraChanged={onCameraChanged ?? ownCamera.handleCameraChanged}
        />
      </APIProvider>
    </div>
  )
}
